import React from 'react';
import { Box, Divider, Link, Typography } from '@mui/material';
import PageSeo from '../seo/PageSeo';
import { SITE_NAME, SITE_URL, pagePathToUrl, toAbsoluteImageUrl } from '../seo/constants';
import PageLayout from '../components/PageLayout';
import { CONTACT_EMAIL, SOCIAL_PROFILE_URLS } from '../site/contact';

const sourceLinkSx = {
    color: 'primary.main',
    textDecoration: 'underline',
    '&:hover': { color: 'primary.dark' },
};

const AUSTIN_PETS_ALIVE_URL = 'https://www.austinpetsalive.org/';

const PAGE_PATH = '/about-us';

type AboutPhoto = {
    src: string;
    alt: string;
    caption: string;
    width: number;
    height: number;
    layout: 'landscape' | 'portrait' | 'square';
};

const ABOUT_PHOTOS: AboutPhoto[] = [
    {
        src: '/RachelEleanorDogShow.jpg',
        alt: 'Eleanor and Rachel stacking two black standard poodles at an AKC dog show',
        caption: 'Eleanor and Rachel with standard poodles at a dog show',
        width: 934,
        height: 1024,
        layout: 'portrait',
    },
    {
        src: '/RachelEleanorFounders.jpg',
        alt: 'Eleanor and Rachel, founders of Galia Standard Poodles, smiling together on a boat with the ocean behind them',
        caption: 'Eleanor and Rachel, founders of Galia Standard Poodles in Austin, TX',
        width: 960,
        height: 960,
        layout: 'square',
    },
    {
        src: '/RachelEleanorWeddingAisle.jpg',
        alt: 'Rachel and Eleanor walking down the aisle at their wedding, holding bouquets',
        caption: 'Rachel and Eleanor at their wedding reception in Austin, TX',
        width: 1024,
        height: 683,
        layout: 'landscape',
    },
    {
        src: '/RachelEleanorWeddingPoodle.jpg',
        alt: 'Black standard poodle in a bow tie at Rachel and Eleanor\'s wedding ceremony',
        caption: 'Standard poodle in formal attire at Rachel and Eleanor\'s wedding',
        width: 682,
        height: 1024,
        layout: 'portrait',
    },
    {
        src: '/RachelEleanorWeddingPortrait.jpg',
        alt: 'Rachel and Eleanor with their black standard poodle at their wedding',
        caption: 'Rachel and Eleanor with their standard poodle on their wedding day',
        width: 682,
        height: 1024,
        layout: 'portrait',
    },
];

const DOG_SHOW_PHOTO = ABOUT_PHOTOS[0];
const FOUNDERS_PHOTO = ABOUT_PHOTOS[1];
const WEDDING_LANDSCAPE = ABOUT_PHOTOS[2];
const WEDDING_PORTRAITS = ABOUT_PHOTOS.filter(
    (photo) => photo.src.startsWith('/RachelEleanorWedding') && photo.layout === 'portrait',
);

const aboutPhotoSx = {
    display: 'block',
    width: '100%',
    height: 'auto',
    borderRadius: 2,
};

const srOnlyFigcaptionSx = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
};

const aboutImageJsonLd = ABOUT_PHOTOS.map((photo) => ({
    '@type': 'ImageObject',
    contentUrl: toAbsoluteImageUrl(photo.src),
    url: toAbsoluteImageUrl(photo.src),
    name: photo.caption,
    description: photo.alt,
    caption: photo.caption,
    width: photo.width,
    height: photo.height,
}));

const aboutPhotoGallerySx = {
    my: 2.5,
    maxWidth: 440,
    mx: 'auto',
    textAlign: 'center',
} as const;

type AboutPhotoFigureProps = {
    photo: AboutPhoto;
    sx?: Record<string, unknown>;
};

const AboutPhotoFigure: React.FC<AboutPhotoFigureProps> = ({ photo, sx }) => (
    <Box component="figure" sx={{ m: 0, position: 'relative', ...sx }}>
        <Box
            component="img"
            src={photo.src}
            alt={photo.alt}
            title={photo.caption}
            width={photo.width}
            height={photo.height}
            loading="lazy"
            decoding="async"
            sx={aboutPhotoSx}
        />
        <Typography component="figcaption" sx={srOnlyFigcaptionSx}>
            {photo.caption}
        </Typography>
    </Box>
);

const AboutUs: React.FC = () => {
    const pageTitle = 'About Us | Galia Standard Poodles | Austin, TX';
    const pageDescription =
        'Meet Eleanor and Rachel — queer-owned standard poodle breeders in Austin, TX. AKC/UKC show handling, professional grooming, CHIC health testing, and Austin Pets Alive! partners.';
    const pageUrl = pagePathToUrl(PAGE_PATH);
    const primaryAboutImage = aboutImageJsonLd.find(
        (image) => image.contentUrl === toAbsoluteImageUrl(WEDDING_LANDSCAPE.src),
    );

    return (
        <>
            <PageSeo
                title={pageTitle}
                description={pageDescription}
                path={PAGE_PATH}
                image={WEDDING_LANDSCAPE.src}
                imageAlt={WEDDING_LANDSCAPE.alt}
                jsonLd={[
                    {
                        '@context': 'https://schema.org',
                        '@type': 'AboutPage',
                        name: pageTitle,
                        description: pageDescription,
                        url: pageUrl,
                        inLanguage: 'en-US',
                        isPartOf: {
                            '@type': 'WebSite',
                            name: SITE_NAME,
                            url: SITE_URL,
                        },
                        about: [
                            { '@type': 'Thing', name: 'Standard Poodle breeding' },
                            { '@type': 'Thing', name: 'Dog rescue' },
                            { '@type': 'Thing', name: 'Austin Pets Alive!' },
                        ],
                        primaryImageOfPage: primaryAboutImage,
                        image: aboutImageJsonLd,
                        mainEntity: {
                            '@type': 'LocalBusiness',
                            name: SITE_NAME,
                            url: SITE_URL,
                            image: aboutImageJsonLd.map((image) => image.contentUrl),
                            description: pageDescription,
                            email: CONTACT_EMAIL,
                            address: {
                                '@type': 'PostalAddress',
                                addressLocality: 'Austin',
                                addressRegion: 'TX',
                                addressCountry: 'US',
                            },
                            sameAs: [...SOCIAL_PROFILE_URLS],
                            founder: [
                                { '@type': 'Person', name: 'Eleanor' },
                                { '@type': 'Person', name: 'Rachel' },
                            ],
                        },
                    },
                    {
                        '@context': 'https://schema.org',
                        '@type': 'BreadcrumbList',
                        itemListElement: [
                            {
                                '@type': 'ListItem',
                                position: 1,
                                name: 'Home',
                                item: `${SITE_URL}/`,
                            },
                            {
                                '@type': 'ListItem',
                                position: 2,
                                name: 'About Us',
                                item: pageUrl,
                            },
                        ],
                    },
                    {
                        '@context': 'https://schema.org',
                        '@type': 'ItemList',
                        name: 'About Galia Standard Poodles — founders and wedding photos',
                        description:
                            'Photos of Eleanor and Rachel with their standard poodles — at dog shows, on their wedding day, and as founders of Galia Standard Poodles in Austin, TX.',
                        itemListElement: aboutImageJsonLd.map((image, index) => ({
                            '@type': 'ListItem',
                            position: index + 1,
                            item: image,
                        })),
                    },
                ]}
            />
            <PageLayout>
                <Typography component="h1" variant="h4" gutterBottom>
                    About Us
                </Typography>
                <Typography variant="body1" paragraph>
                    Galia Standard Poodles is Eleanor and Rachel, a married queer couple based in Austin, TX. We met
                    at a dog show and have always bonded over dogs. Eleanor owner-handles our dogs in AKC and UKC show
                    rings, and she is a self-taught groomer who has worked professionally. That hands-on experience
                    shapes how we maintain coats, start grooming desensitization early, and prepare puppies for
                    lifelong care at home. We raise our dogs in our Austin home, with health testing, socialization,
                    and thoughtful placement at the heart of what we do.
                </Typography>

                <Box sx={aboutPhotoGallerySx}>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1.5,
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            mb: 1.5,
                        }}
                    >
                        <AboutPhotoFigure photo={DOG_SHOW_PHOTO} sx={{ width: '46%', maxWidth: 190 }} />
                        <AboutPhotoFigure photo={FOUNDERS_PHOTO} sx={{ width: '46%', maxWidth: 190 }} />
                    </Box>
                    <AboutPhotoFigure photo={WEDDING_LANDSCAPE} sx={{ maxWidth: '100%', mb: 1.5 }} />
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1.5,
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                        }}
                    >
                        {WEDDING_PORTRAITS.map((photo) => (
                            <AboutPhotoFigure
                                key={photo.src}
                                photo={photo}
                                sx={{ width: '46%', maxWidth: 190 }}
                            />
                        ))}
                    </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Typography component="h2" variant="h5" gutterBottom>
                    Rescue and Community
                </Typography>
                <Typography variant="body1" paragraph>
                    Long before Galia, both of us were involved in rescue. We have fostered and rehomed kittens,
                    puppies, and dogs over the years, and we have often partnered with{' '}
                    <Link
                        href={AUSTIN_PETS_ALIVE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={sourceLinkSx}
                    >
                        Austin Pets Alive!
                    </Link>
                    . That work matters deeply to us. Responsible breeding and rescue are not opposites — we believe
                    in doing our part to support animals who need homes while breeding thoughtfully and transparently.
                    We vow to donate a small percentage of each puppy&apos;s fee to rescues, and we remain active in
                    the community.
                </Typography>
            </PageLayout>
        </>
    );
};

export default AboutUs;
